/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import 'ag-grid-enterprise';

import * as $ from 'jquery';

import 'select2';
import {DataParams, Event, SearchOptions} from 'select2';

import ClickEvent = JQuery.ClickEvent;
import KeyUpEvent = JQuery.KeyUpEvent;
import KeyDownEvent = JQuery.KeyDownEvent;
import {ICellEditorComp, ICellEditorParams} from "ag-grid-community";

/**
 *
 * @class WorkActiveDaysEditor
 *
 * @brief dynamic dropdown list for day numbers (in simulation time).  We treat a day number
 * as a "tag" so users can just drop the pull down and type or pick the tags (days).
 *
 * We use Select2: https://select2.org  for the "combo box".
 *
 * @note The select2 @types/select2 (definitely typed stuff), includes "requirejs" which is
 * not going to be compatible with Angular.  Just edit its index.d.ts file and remove the
 * requirejs stuff after you add it to your npm repo.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
export class WorkActiveDaysEditor implements ICellEditorComp {

  /** agGrid DOM element for display */
  public gui: HTMLElement;

  /** the callback to tell agGrid we're done editing */
  private editingDone: (suppressNavigateAfterEdit?: boolean) => void = null;

  private clickListener: (e: MouseEvent) => void = null;

  private tabBlocker: (e: KeyboardEvent) => void = null;

  /** the DOM element (<select>) being wrapped with a combo box */
  private             el: JQuery            = null;

  private value: number[] = [];

  /** the complete set of data items */
  private           data: any[]             = [];

  /** the current matched data item (or all) */
  private        matched: any[]             = [];

  /** we keep the drop-down open all the time, but sometimes it really needs to close */
  private     allowClose: boolean           = false;

  /** gate to make sure we don't try to selectAll() from within selectAll() */
  private      reOpening: boolean           = false;

  /** the click location */
  private    startMarker: HTMLOptionElement = null;

  /** the shift+click location */
  private      endMarker: HTMLOptionElement = null;

  /** set up the custom editor (happens on every edit attempt) */
  public init(params: ICellEditorParams): void {

    const ad       = this;
    ad.editingDone = params.stopEditing;

    /* opened with Delete? */

    if ((params.eventKey ?? '').length === 1 && params.eventKey.charCodeAt(0) === 46) {

      ad.value = [];
    }

    /* use existing cell value if there is one */

    if (params.value) {

      ad.value = params.value;
    }

    /* set up the Select2 drop down ... */

    ad.gui          = document.createElement('div');
    ad.gui.tabIndex = -1;

    let options: string = '<option></option>';

    /* walk the decision points, generating a select option for each */

    for (const dayNum of ad.value) {

      if (isNaN(dayNum)) {

        continue;
      }

      ad.data.push(dayNum);

      options += `  <option value="${dayNum}" selected="selected">${dayNum}</option>`;
    }

    /* insert new HTML for our custom select ... */

    const html = `
<div class="accept-button">
  <i class="fas fa-check"></i>
</div>
<select class="work-blox-day-select"  multiple="multiple">
${options}
</select>`;

    $(ad.gui).html(html);

    /* add "accept" behavior; clicking the accept button */

    $(ad.gui).find('.accept-button').on('click', (e: ClickEvent): void => {

      e.preventDefault();
      e.stopPropagation();

      /* tell agGrid we're done editing */

      ad.editingDone(true);
    });

    /* now create a Select2 version of it, that supports both selection and type ahead ... */

    ad.el = $(ad.gui).find('select.work-blox-day-select');

    $(ad.el).on('select2:select', (e: Event<HTMLElement, DataParams>) => {

      const   selBox: JQuery = $(ad.gui).find('.select2-selection input');

      $(selBox).trigger('focus');

      /* update the current day list (whatever is now in the select list */

      ad.value = [];

      $(ad.el).find(':selected').each((index, option) => {

        const value: number = parseInt( $(option).val() as string, 10);

        if (!value || isNaN(value)) {

          return  ;
        }

        ad.value.push(value);
      });

      /*
       * to handle range selection (i.e. SHIFT + Click), we have to be stateful;
       * every-time they click, we set a start marker to that position, but if
       * it was a SHIFT click, we check and if we have a start marker, then we
       * have a range to use for bulk selection.
       *
       * The only weird part is that this is all after the fact, clicking has
       * already happened, and the select2() combo box has already done what
       * it does on clicking/selection.  So, the range is "internal" to the
       * two markers, i.e. it doesn't include the two item that were (shift)
       * clicked...they've already been processed. So we just want the range
       * "inside".
       *
       */

      this.rangeSelect(e);
    });

    $(ad.el).on('select2:unselect', (e: Event<HTMLElement, DataParams>) => {

      const   selBox: JQuery = $(ad.gui).find('.select2-selection input');

      $(selBox).trigger('focus');

      /* update the current day list (whatever is now in the select list */

      ad.value = [];

      $(ad.el).find(':selected').each((index, option) => {

        const value: number = parseInt( $(option).val() as string, 10);

        if (!value || isNaN(value)) {

          return  ;
        }

        ad.value.push(value);
      });

      /* similar to select, check for range selection */

      this.rangeSelect(e);
    });

    $(ad.el).select2({

      placeholder: `Select...`,

      multiple:      true,
      tags:          true,
      tokenSeparators: [',', ' '],
      allowClear:    false,
      closeOnSelect: false,
      width:         '200px',

      /* only allow numbers as tags (days) */
      createTag:  (args: SearchOptions): any => {

        if (!args.term.match(/^\d+$/)) {

          /* not a number */

          return null;
        }

        /* allow the new tag */

        return {
          id: args.term,
          text: args.term
        };
      },

      data: ad.data
    });

    const ctrlAFN = (e: KeyUpEvent|KeyDownEvent) => {

      if((e.key.toLowerCase() === 'a') && (e.ctrlKey)) {

        e.preventDefault();
        e.stopPropagation();

        if ( ad.reOpening === false) {

          ad.selectAll();

        }
      }

    };

    const escCheckFN = (e: KeyUpEvent) => {

      if (e.key.toLowerCase() === 'escape') {

        /* escape! */

        e.preventDefault();
        e.stopPropagation();

        ad.editingDone(true);

      }

    };

    $(ad.gui).off('keyup', ctrlAFN);
    $(ad.gui).on('keyup', ctrlAFN);

    $(ad.el).off('keyup', ctrlAFN);
    $(ad.el).on('keyup', ctrlAFN);

    $(document).off('keyup', 'input.select2-search__field', ctrlAFN);
    $(document).on('keyup', 'input.select2-search__field', ctrlAFN);

    $(ad.gui).off('keyup', escCheckFN);
    $(ad.gui).on('keyup', escCheckFN);

    $(ad.el).on('select2:selecting', (e) => {

      // @ts-ignore
      if (e.params.args.data.id === '') {

        e.preventDefault();

        return ;
      }

      /*
       * select2 seems to have a bug:
       *
       *   https://forums.select2.org/t/pressing-enter-is-creating-the-tag-but-keeping-the-text/120
       *
       * when you are typing in a new/existing tag, and press ENTER instead of SPACE (to add the tag)
       * it will create the tag (i.e. add to your multi-selection of tags), but also retain the last
       * thing you typed...so now you another duplicate/partial tag is in the input, which was not
       * intended.
       *
       * not sure why it does that, there doesn't seem to be any resolution.  But, maybe we can at
       * least clear the input box, after we've selected something...so the duplication goes away.
       *
       */

      let inputBox: JQuery<HTMLElement> = $('.select2.select2-container .select2-search__field');

      $(inputBox).val('');

    });

    $(ad.el).on("select2:closing", function(e) {

      const selBox: JQuery = $(ad.gui).find('.select2-selection input');

      $(selBox).trigger('focus');

      if (ad.allowClose === true) {

        return ;
      }

      e.preventDefault();

      // @ts-ignore
      if (typeof e.params.args.originalEvent !== 'undefined') {

        // @ts-ignore
        if ((e.params.args.originalEvent.key === 'Escape') || (e.params.args.originalEvent.key === 'Tab')) {

          /* the pressed Escape while clicking items in the drop-down, we're done */

          ad.editingDone(true);

          return;
        }
      }

      $(ad.el).select2('open');

    });

    $(ad.el).on("select2:closed", function() {

      $(ad.el).select2("open");
    });

    $(ad.el).on('select2:close', () => {

      $(ad.el).trigger('focus');

    });

    $(ad.el).on("select2:open", function() {

      /*
       * once the dropdown is open, make sure the selection area has the focus so if you press ENTER
       * we can finish editing.
       *
       */

      const selBox: JQuery = $('input.select2-search__field');

      $(selBox).css('width', 'auto');

      if ((params.eventKey ?? '').length === 1) {

        $(selBox).val(params.eventKey);
      }

      $(selBox).trigger('focus');

    });

    /* auto-open */

    setTimeout(() => {

      $(ad.el).prop('tabIndex', -1);

      $(ad.el).select2('open');

      //const input = $(ad.gui).find('.select2-search__field, .select2-selection--multiple');

      $(ad.gui).on('keyup', (e: KeyUpEvent) => {

        if (e.key !== 'Tab') {

          return ;
        }

        ad.editingDone(true);

      });

      $(ad.gui).on('keydown', (e: KeyDownEvent) => {

        if ((e.key !== 'Tab') && (e.key !== 'Enter')) {

          return ;
        }

        ad.editingDone(true);

        e.preventDefault();
        e.stopPropagation();
      });

    }, 50);

    /*
     * outside our editor, block the Tab key while the editor is up, so they can't
     * mistakenly tab to the address bar or other browser controls and reload the page.
     *
     */
    ad.tabBlocker = (e: KeyboardEvent) => {

      if ((e.key === 'Tab') || (e.key === 'Escape')) {

        e.preventDefault();
        e.stopPropagation();

        return false;
      }

    };

    /*
     * watch for clicks outside the editor, implies closing the editor; the editor popups
     * are modal, there should only be one at a time.
     *
     */

    ad.clickListener = (e: MouseEvent) => {

      let target = e.target;

      do {

        if ($(target).hasClass(`select2-container`)) {

          /* it's ok, somewhere inside */

          return;
        }

        if ($(target).hasClass('select2-selection__choice')) {

          /* it's ok, somewhere inside */

          return;
        }

        if (target === ad.gui) {

          /* it's ok, somewhere inside */

          return;
        }

        // @ts-ignore
        target = target.parentNode;

      } while (target);

      /* click is somewhere outside, auto-close */

      document.removeEventListener('click', ad.clickListener);

      ad.editingDone(true);
    };

    document.addEventListener('click', ad.clickListener);

    document.addEventListener('keypress', ad.tabBlocker);

  }

  /** helper to update/execute ranage selection from click to shift + click */
  public rangeSelect(e: Event<HTMLElement, DataParams>) {

    /*
     * note: called from both select and unselect! Our job here is to invert the selction
     * status of the items between (and not including) the markers.
     *
     */

    const ce = this;

    if (typeof e.params.originalEvent !== 'undefined') {

      if (e.params.originalEvent.type === 'mouseup') {

        // @ts-ignore
        if (e.params.originalEvent.shiftKey === true) {

          /* are ending a range? */

          if (ce.startMarker === null) {

            /* no range yet */

            return ;
          }

          ce.endMarker = e.params.data.element;

          if (ce.endMarker == ce.startMarker) {

            /* empty range */

            ce.startMarker = null;
            ce.endMarker   = null;

            return ;
          }

          /* make sure they are in the right order */

          if (ce.startMarker.index > ce.endMarker.index) {

            const tmp: HTMLOptionElement = ce.startMarker;

            ce.startMarker = ce.endMarker;
            ce.endMarker   = tmp;
          }

          /* fetch the data items that are in that range */

          const range: HTMLOptionElement[] =
            Array.from(ce.startMarker.parentElement.children).filter((el: Element, index: number) => {

              return ((index > ce.startMarker.index) && (index < ce.endMarker.index));
            }) as HTMLOptionElement[];

          const rIds: number[] = range.map(x => parseInt(x.value));

          /*
           * fetch the overall selection, we want to modify (invert) the selection where it
           * overlaps with our range; if current selection doesn't include an item in our
           * range, then we add it.  Otherwise, we remove it.  For items outside our
           * current range, no touchy.
           *
           */

          const   selected: any[]    = $(ce.el).select2('data');
          let    newSelect: string[] = selected.map(x => x.id);

          for (let i: number=0; i < ce.value.length; i++) {

            const id: number = ce.value[i];

            if ( !rIds.includes(id) ) {

              /* this one is outside the interior selection range */

              continue;
            }

            const isSelected: boolean = (typeof selected.find(x => x.id == id) !== 'undefined');

            if (!isSelected) {

              /* if it's not selected we have to add it */

              newSelect.push(id.toString());

            } else {

              /* otherwise filter it out of the selection */

              newSelect = newSelect.filter(x => x != id.toString());
            }

          } /* end of selection adjusting */

          /* now that the selection is merged, do a bulk select, similar to selectAll() */

          ce.setSelection(newSelect);

          /* we processed a bulk selection, reset */

          ce.startMarker = null;
          ce.endMarker   = null;

        } else {

          ce.startMarker = e.params.data.element;

        }
      }
    }
  }

  public selectAll(): void {

    const ce              = this;
    let   selected: any[] = $(ce.el).select2('data');
    const results: JQuery = $('.select2-results li.select2-results__option');

    /* first, figure out what the contents of the drop-down are ... */

    ce.matched = [];

    $(results).each((index, element) => {

      /* convert result item to its #id; select2 encodes the data id at the end of the #id */

      const  id: string   = $(element).attr('id');
      const cap: string[] = id.match(/-(\d+)$/);

      if (Array.isArray(cap) && (cap.length >= 2)) {

        /* extract the data id, and lookup the actual item ... */

        const item: number = ce.value.find(x => x === parseInt(cap[1]));

        if ((typeof item !== 'undefined') && (item !== null)) {

          ce.matched.push({
            id:       item,
            text:     item.toString(),
            selected: true
          });

        } else {

          ce.matched.push({
            id:       parseInt(cap[1]),
            text:     cap[1],
            selected: false
          });
        }

      }

    });

    /* we know what's in the dropdown and which ones are already selected or not */

    /* we want to merge in all the ids...and de-duplicate */

    const a: number[] = selected.map(x => parseInt(x.id));
    const b: number[] = ce.matched.map(x => x.id);

    const c: number[] = a.concat(b.filter((item) => a.indexOf(item) < 0));
    const d: string[] = c.map(x => x.toString());

    this.setSelection(d);
  }

  /** helper to force the selection to be those items with these value (ids) */
  public setSelection(sel: string[]) {

    const ce = this;

    /* clear the selection */

    $(ce.el).val([]);
    ce.value = [];

    /* set the merged selection */

    $(ce.el).val(sel).trigger('change.select2');

    /* make sure the drop-down is re-painted to show the new selection */

    const selected: any[] = $(ce.el).select2('data');

    selected.forEach((item) => {

      $(ce.el).trigger({
        type: 'select2:select',
        params: {
          data: item
        }
      });

    });

    ce.allowClose = true;
    $(ce.el).select2('close');
    ce.allowClose = false;

    ce.reOpening = true;
    $(ce.el).select2('open');
    ce.reOpening = false;

    const selBox: JQuery = $(ce.gui).find('.select2-selection input');

    $(selBox).trigger('focus');

    $(selBox).val('');
  }

  /** focus on local editing control (after it's attached to agGrid) */
  public afterGuiAttached(): void {

  }

  /* Return the DOM element of your editor, this is what the grid puts into the DOM */
  public getGui(): HTMLElement {

    return this.gui;
  }

  /* Should return the final value to the grid, the result of the editing */
  public getValue(): number[] {

    return this.value.sort((a: number, b: number) => {

      return a - b;
    });
  }

  /** do any clean up */
  destroy(): void {

    /* clear out our loss of focus listener */

    document.removeEventListener('click', this.clickListener);

    document.removeEventListener('keypress', this.tabBlocker);

    /* deep six the select drop-down */

    const el: JQuery = $(this.gui).find('select.work-blox-day-select');

    $(el).select2('destroy');

    /* remove the DOM we inserted */

    $(this.gui).remove();
  }

  /** return true to be placed in a popup */
  public isPopup(): boolean {

    return true;
  }

  /** return the positioning, either 'over' or 'under' (only for popups) */
  getPopupPosition(): 'over' | 'under' | undefined  {

    return 'over';
  }

  /** return true to disallow editing before it even starts */
  isCancelBeforeStart(): boolean {

    return false;
  }

  /** return true to ignore editing change */
  isCancelAfterEnd(): boolean {

    return false;
  }

  /** on tabbing into the cell */
  public focusIn(): boolean {

    return true;
  }

  /** on tabbing out of the cell */
  public focusOut(): boolean {

    return true;
  }
}
